import { z } from 'zod';
export const INTAKE_FIELD_KINDS = [
    'name',
    'email',
    'phone',
    'address',
    'service_picker',
    'text',
    'long_text',
    'select',
    'boolean',
    'date',
];
export const intakeFieldSchema = z
    .object({
    key: z.string().regex(/^[a-z][a-z0-9_]{0,59}$/),
    kind: z.enum(INTAKE_FIELD_KINDS),
    label: z.string().trim().min(1).max(120),
    required: z.boolean().default(false),
    placeholder: z.string().trim().max(160).optional(),
    help: z.string().trim().max(240).optional(),
    options: z.array(z.string().trim().min(1).max(80)).max(30).optional(),
})
    .superRefine((field, ctx) => {
    if (field.kind === 'select' && !field.options?.length) {
        ctx.addIssue({
            code: 'custom',
            path: ['options'],
            message: 'Select fields need at least one option.',
        });
    }
    if (field.kind !== 'select' && field.options?.length) {
        ctx.addIssue({
            code: 'custom',
            path: ['options'],
            message: 'Options are only valid for select fields.',
        });
    }
});
export const intakeFormSpecSchema = z
    .object({
    schema_version: z.literal(1),
    intent: z.enum(['contact', 'quote', 'order']),
    heading: z.string().trim().min(1).max(120),
    intro: z.string().trim().max(1000).default(''),
    submit_label: z.string().trim().min(1).max(60).default('Send request'),
    fields: z.array(intakeFieldSchema).min(1).max(30),
    confirmation: z.object({
        heading: z.string().trim().min(1).max(120),
        body: z.string().trim().min(1).max(1000),
    }),
})
    .superRefine((spec, ctx) => {
    const keys = new Set();
    for (const [index, field] of spec.fields.entries()) {
        if (keys.has(field.key)) {
            ctx.addIssue({
                code: 'custom',
                path: ['fields', index, 'key'],
                message: 'Field keys must be unique.',
            });
        }
        keys.add(field.key);
    }
    if (!spec.fields.some((field) => field.kind === 'name')) {
        ctx.addIssue({
            code: 'custom',
            path: ['fields'],
            message: 'Every request form needs a name field.',
        });
    }
    if (!spec.fields.some((field) => field.kind === 'email' || field.kind === 'phone')) {
        ctx.addIssue({
            code: 'custom',
            path: ['fields'],
            message: 'Every request form needs an email or phone field.',
        });
    }
});
export const STARTER_INTAKE_FORM = {
    schema_version: 1,
    intent: 'quote',
    heading: 'Request service',
    intro: 'Tell us what you need and we’ll get back to you.',
    submit_label: 'Send request',
    fields: [
        { key: 'name', kind: 'name', label: 'Your name', required: true },
        { key: 'email', kind: 'email', label: 'Email', required: true },
        { key: 'phone', kind: 'phone', label: 'Phone', required: false },
        { key: 'address', kind: 'address', label: 'Service address', required: false },
        { key: 'services', kind: 'service_picker', label: 'What can we help with?', required: false },
        { key: 'notes', kind: 'long_text', label: 'Tell us more', required: false },
    ],
    confirmation: {
        heading: 'Request received',
        body: 'Thanks — we’ll review your request and get back to you.',
    },
};
export const INTAKE_GUIDE = `Public request intake starts hidden and becomes useful when an owner publishes one starter form. Every submission lands in a review inbox, customer matching is explainable, and creating a lead, draft quote or draft job is an explicit governed conversion; advanced fields, intent and routing stay dormant until configured. UI, MCP, RPC and public ingress share tenant permissions, validation, idempotency and audit evidence.`;
//# sourceMappingURL=intake.js.map